import z from 'zod'

import type { InputSchema } from '@/components/forms/schema-form/types'

export type IntegrationDTO = {
	connectors: Array<IntegrationConnector>
}

export type IntegrationDefinitionDTO = {
	connector: Integration
	status_checks: Array<IntegrationStatusChecks>
}

export type IntegrationEventTypesDTO = {
	connector_id: string
	connector_name: string
	events_enabled: boolean
	event_types: Array<IntegrationEventType>
}

export type IntegrationEventType = {
	event_type: string
	enabled: boolean
	// `config` replaces the legacy `options` field — per-event schema-validated payload (ADR 0004)
	config?: Record<string, unknown> | null
}

// Wire shape for a single event type entry in the events_config envelope
// (mirrors ConnectorEventsConfig.py). Identical to the read DTO entry by
// contract — aliased so the write and read shapes can never drift apart.
export type EventTypeConfiguration = IntegrationEventType

// The envelope sent to the backend for all event-config writes
export type ConnectorEventsConfiguration = {
	event_types: Array<EventTypeConfiguration>
}

export type IntegrationStatus =
	'success' | 'failed' | 'degraded' | 'pending' | 'error' //  Only applies to status from Integration Details

export type IntegrationHealthStatus = 'healthy' | 'error' | 'degraded' // Only applies to status from Integration List

export type IntegrationConnector = {
	connector: Integration
	state: IntegrationState
}

export type Integration = {
	default: boolean
	definition_id: string
	enabled: boolean
	events_enabled: boolean
	events_options: IntegrationEventsOptions | null
	id: string
	// Null on every connector in the 2026-07-02 capture (health/freshness live in
	// the list wrapper's `state` envelope) — widened per wire truth.
	last_successful_connection: string | null
	last_updated: string
	metrics_enabled: boolean
	metrics_options: any
	name: string
	state: any // This is not the same as the IntegrationState type, currently swagger is not updated so we can't type this correctly
	tenant: string
}

export type IntegrationState = {
	last_checked: string
	status: IntegrationHealthStatus
}

export type IntegrationStatusChecks = {
	check_subtype: string | ''
	check_type: 'health_check' | 'event_collection' | 'overall_health'
	connector_id: string
	message: string
	status: IntegrationStatus
	status_metadata:
		| {
				collection_duration_seconds: number
				collection_job_id: string
				error_message: string
		  }
		| object
		| null
	timestamp: string
}

export type IntegrationEventsOptions = {
	enabled: boolean
	options: unknown
	event_type: string
}

/**
 * Backend connector taxonomy (`ConnectorCategory`) — a closed 25-value enum,
 * nullable on the wire. Modeled as a union (not a free string) so a typo'd
 * category fails typecheck. Canon: the integrations wire-truth reference doc
 * (knowledge/reference/integrations-wire-truth.md).
 */
export type IntegrationCategory =
	| 'AI Governance & Audit'
	| 'CDN'
	| 'Cloud Platform'
	| 'Data Security / DLP'
	| 'DNS / DDI / IPAM'
	| 'EDR'
	| 'Email Security'
	| 'Firewall / NGFW'
	| 'Identity & Access'
	| 'IDS / IPS'
	| 'Load Balancers'
	| 'Log Collection / Forwarding'
	| 'Network Access Control (NAC)'
	| 'Network Detection & Monitoring'
	| 'Physical / Peripheral / Facilities'
	| 'Privileged Access & Password Mgmt'
	| 'RMM'
	| 'SaaS'
	| 'Storage & Backup'
	| 'Switch/Router'
	| 'Virtualization'
	| 'VPN'
	| 'Vulnerability Management'
	| 'Web Proxy'
	| 'Wireless / Access Points'

/** Backend `IntegrationType` — server defaults new definitions to 'Cloud'. */
export type IntegrationType = 'Cloud' | 'Network Log'

/**
 * Where a log source's records come from: `api` is a connector the backend
 * polls (the `available_integrations` catalogue), `agent` a Collector listener
 * (the `agent-log-source-options` catalogue). Named as `GET logs/sources`
 * names it.
 */
export type LogSourceClass = 'api' | 'agent'

/**
 * One row of `GET logs/sources` (air-backend `log_discovery.py` `_source_row`,
 * `TenantLogSourceDTO`): a Listed Source — a connector, a network listener, or
 * the shared Unparsed syslog row. Every row the tenant has comes back, active
 * or not.
 */
export type ListedSource = {
	tenant: string
	/** The connector or listener id; `null` only on the Unparsed syslog row. */
	log_source_id: string | null
	definition_id: string
	/** The instance's name, not its definition's. */
	name: string
	source_class: LogSourceClass
	active: boolean
	/** Every event type the definition declares. */
	event_types: Array<string>
}

export type ListedSourcesResponse = {
	sources: Array<ListedSource>
}

/**
 * One definition from `GET available_integrations` — wire shape, every field
 * the backend sends (air-backend feature/syslog
 * `automation_runtime/automation_service.py:139-155`).
 */
export type AvailableIntegration = {
	id: string
	vendor: string
	product: string
	version: string
	name: string
	description: string
	event_types: Array<AvailableIntegrationEventTypes>
	visible: boolean
	capabilities: Array<IntegrationCapability>
	category: IntegrationCategory | null
	/** Null when the definition leaves it unset. */
	integration_type: IntegrationType | null
}

export type AvailableIntegrationEventTypes = {
	name: string
	description: string
}

export type IntegrationDefinition = {
	id: string
	name: string
	description: string
	vendor: string
	product: string
	version: string
	visible: boolean
	config_schema: InputSchema
	can_fetch_events: boolean
	can_test_connection: boolean
	// Record keyed by event-type string (e.g. "cloudtrail") — only schema'd event types are present
	event_options_schema: Record<string, InputSchema> | null
	event_types: Array<AvailableIntegrationEventTypes>
	can_fetch_metrics: boolean
	metric_options_schema: InputSchema | null
	capabilities: Array<IntegrationCapability>
	category: IntegrationCategory | null
	integration_type: IntegrationType
}

export type NewIntegrationFormData = {
	name: string
	definition_id: string
	connection_config: Record<string, any>
	enabled: boolean
	events_enabled?: boolean
	events_config?: ConnectorEventsConfiguration
}

export type EditIntegrationFormData = Partial<NewIntegrationFormData>

export type ValidateIntegrationFormData = {
	success: boolean
	message: string
	details: Record<string, any>
}

export type IntegrationCapability =
	'Log Ingestion' | 'Response' | 'Log Enrichment' | 'Platform'

// Enriched Integrations are a combination of Available and Tenant Integrations
export type EnrichedIntegration = {
	id: string
	name: string
	definition_id: string
	definition_name: string
	vendor: string
	product: string
	event_types: Array<AvailableIntegrationEventTypes>
	capabilities: Array<IntegrationCapability>
	// Raw wire category from the matched catalog entry (`null` → renders under
	// "Other"). Never a display-group label — rows/KPIs map it at render time.
	category: IntegrationCategory | null
	// Whether this connector is the tenant's default for its definition.
	default: boolean
	state: IntegrationState
}

/**
 * Microsoft oAuth Flow
 */

export type MicrosoftConsentURLResponse = {
	consent_url: string
	expires_in: number // in seconds
}

export type ConsentSuccess = {
	admin_consent: string
	tenant: string
	state: string
}

export type ConsentError = {
	error: string
	error_description?: string
	error_subcode?: string
	error_uri?: string
	full_search?: string
}

export type ConsentCallbackData = {
	type: 'MICROSOFT_CONSENT_CALLBACK' | string
	data?: ConsentSuccess // Success returns key data and no error
	error?: ConsentError // Error returns key error and no data
}

export const microsoftConsentCallbackSearchSchema = z.object({
	// Success parameters
	admin_consent: z.string().optional(),
	tenant: z.string().optional(),
	state: z.string().optional(),
	scope: z.string().optional(),
	// Error parameters
	error: z.string().optional(),
	error_description: z.string().optional(),
	error_subcode: z.string().optional(),
	error_uri: z.string().optional(),
})

export type MicrosoftConsentCallbackSearch = z.infer<
	typeof microsoftConsentCallbackSearchSchema
>

export type VerifyMicrosoftConsentCallbackData = {
	state: string
	tenant: string
	air_tenant_id: string
	definition_id?: string
}
